import React, { createContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { API_CONFIG } from "../api/apiUrl";

interface User {
    id: number;
    login: string;
    firstName?: string;
}

interface UserProfile {
    id: number;
    mobile: number;
    isVerified: boolean;
    uniqueId: string;
    user: User;
}
interface PlotOwner {
    id: number;
    user: User;
}
interface Plot {
    plot: {
        id: number;
        plotSize: number;
        plotLocation: string;
        plotFloors: number;
        plotFacing: string;
        plotRooms: number;
        remarks: string | null;
        others: string | null;
        createDate: string;
        updateDate: string;
        plotOwner: PlotOwner;

        createdBy: User;
        updatedBy: User;
    };
    files: any[] | null;
    plotServices: any[] | null;
}

interface AuthResponse {
    user: User;
    userProfile: UserProfile;
    authorities: string[];
    plots: Plot[];
}

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    userProfile: UserProfile | null;
    authorities: string[];
    plots: Plot[];
    login: (token: string) => Promise<void>;
    logout: () => void;
    loading: boolean;
    selectedPlotId: number | null; 
    setSelectedPlotId: (plotId: number | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}


export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [user, setUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [authorities, setAuthorities] = useState<string[]>([]);
    const [plots, setPlots] = useState<Plot[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedPlotId, setSelectedPlotId] = useState<number | null>(null);

    const fetchUser = async (token: string) => {
        try {
            const response = await axios.get<AuthResponse>(API_CONFIG.USER_PROFILES, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(response.data.user);
            setUserProfile(response.data.userProfile);
            setAuthorities(response.data.authorities);
            setPlots(response.data.plots || []);
            setIsAuthenticated(true);
        } catch (error) {

            logout();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('id_token');
        if (token) {
            fetchUser(token);
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (token: string) => {
        localStorage.setItem('id_token', token);
        setLoading(true);
        await fetchUser(token);
    };

    const logout = () => {
        localStorage.removeItem('id_token');
        localStorage.removeItem('userLogin');
        setIsAuthenticated(false);
        setUser(null);
        setUserProfile(null);
        setAuthorities([]);
        setPlots([]);
        setLoading(false);
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            userProfile,
            authorities,
            plots,
            login,
            logout,
            loading,
            selectedPlotId,
            setSelectedPlotId,
        }}>
            {children}
        </AuthContext.Provider>
    );
};